/**
 * VP-175 — „Idiotoodporne tworzenie reklam na Meta" (Onboarding Wizard).
 *
 * Najprostszy flow: user kilkoma klikami konfiguruje Meta i uruchamia pierwszą
 * reklamę — CAŁOŚĆ dzieje się w wizardzie, bez przerzucania do CampaignBuildera
 * i bez pytań o cele/KPI/strukturę kampanii.
 *
 *  Faza A (MetaReadiness): połącz Meta → wybierz stronę → wybierz konto reklamowe
 *  (kroki 2 i 3 jako dropdown z wyszukiwarką).
 *  Faza B — dwa tryby, oba kończą się w panelu „przejrzyj i uruchom":
 *    • Z adresu strony (AI): wklej URL → AI pisze teksty i proponuje zdjęcia.
 *    • Ręcznie: user sam podaje teksty, link, budżet i wgrywa zdjęcie.
 *  Launch: POST /api/campaign-creator/launch (SSE) bezpośrednio z wizarda —
 *  cel dobrany za usera (ruch na stronę), targetowanie domyślne.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Globe, PencilLine, Rocket, ArrowLeft } from "lucide-react";
import MetaReadiness, {
  type MetaReadinessContext,
} from "@/components/onboarding-wizard/meta-readiness";
import UrlAdFlow from "@/components/onboarding-wizard/url-ad-flow";
import AdReviewPanel, {
  type AdReviewPrefill,
} from "@/components/onboarding-wizard/ad-review-panel";

type Phase = "meta" | "choose-mode" | "url" | "review";

const EMPTY_PREFILL: AdReviewPrefill = { dailyBudget: 50 };

export default function OnboardingWizard(): JSX.Element {
  const [phase, setPhase] = useState<Phase>("meta");
  const [ctx, setCtx] = useState<MetaReadinessContext | null>(null);
  const [prefill, setPrefill] = useState<AdReviewPrefill>(EMPTY_PREFILL);

  const startReview = (nextPrefill: AdReviewPrefill): void => {
    setPrefill(nextPrefill);
    setPhase("review");
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto px-5 py-8">
        <div className="flex items-center gap-2.5 mb-1.5">
          <div className="w-9 h-9 rounded-xl bg-[#ef3e29]/15 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-[#ef3e29]" />
          </div>
          <h1 className="text-xl font-semibold text-white">Stwórz reklamę na Facebooku</h1>
        </div>
        <p className="text-sm text-white/50 mb-6">
          Przeprowadzimy Cię krok po kroku — w kilka kliknięć.
        </p>

        {phase === "meta" && (
          <MetaReadiness
            onReady={(c) => {
              setCtx(c);
              setPhase("choose-mode");
            }}
          />
        )}

        {phase === "choose-mode" && (
          <div className="space-y-3">
            <p className="text-sm text-white/60">Jak chcesz przygotować reklamę?</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                onClick={() => setPhase("url")}
                className="text-left rounded-xl border border-white/[0.1] bg-white/[0.03] hover:border-[#ef3e29]/40 hover:bg-[#ef3e29]/[0.04] p-4 transition-colors"
              >
                <Globe className="w-5 h-5 text-[#ef3e29] mb-2" />
                <div className="text-sm font-medium text-white">Z adresu strony (AI)</div>
                <div className="text-xs text-white/50 mt-1">
                  Wklej adres swojej strony — AI napisze teksty i zaproponuje zdjęcia. Najszybsze.
                </div>
              </button>
              <button
                onClick={() => startReview(EMPTY_PREFILL)}
                className="text-left rounded-xl border border-white/[0.1] bg-white/[0.03] hover:border-[#ef3e29]/40 hover:bg-[#ef3e29]/[0.04] p-4 transition-colors"
              >
                <PencilLine className="w-5 h-5 text-[#ef3e29] mb-2" />
                <div className="text-sm font-medium text-white">Wpisz ręcznie</div>
                <div className="text-xs text-white/50 mt-1">
                  Sam podaj teksty, link i budżet oraz wgraj zdjęcie.
                </div>
              </button>
            </div>
            <button
              onClick={() => setPhase("meta")}
              className="text-xs text-white/40 hover:text-white/70 flex items-center gap-1 mt-1"
            >
              <ArrowLeft className="w-3 h-3" /> Wróć do konfiguracji Meta
            </button>
          </div>
        )}

        {phase === "url" && (
          <UrlAdFlow onReady={startReview} onBack={() => setPhase("choose-mode")} />
        )}

        {phase === "review" && ctx && (
          <AdReviewPanel
            ctx={ctx}
            prefill={prefill}
            onBack={() => setPhase("choose-mode")}
            onRestart={() => {
              setPrefill(EMPTY_PREFILL);
              setPhase("choose-mode");
            }}
          />
        )}

        {phase === "review" && !ctx && (
          <div className="text-sm text-white/50">
            Najpierw skonfiguruj konto Meta.{" "}
            <Button size="sm" variant="ghost" onClick={() => setPhase("meta")}>
              Wróć
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
